import FieldWidgetUtil from "@/utils/editView/widgets/FieldWidgetUtil.ts";
import ClientCache from "@/utils/login/ClientCache.ts";
import NamaCommon from "@/utils/common/NamaCommon.ts";
import {computed, ComputedRef, Ref} from "vue";
import {useLoadingStore} from "@/stores/LoadingStore.ts";
import {WidgetsPresenter} from "@/utils/presenters/interfaces.ts";
import FieldType from "@/utils/constants/FieldType.ts";
import SearchingAndSuggestionUtil, {SearchForParams} from "@/utils/editView/widgets/SearchingAndSuggestionUtil.ts";
import CommonFieldIds from "@/utils/constants/CommonFieldIds.ts";
import {EditPresenter} from "@/utils/presenters/EditPresenter.ts";
import EditScreenFieldUtils from "@/utils/editView/editScreens/EditScreenFieldUtils.ts";
import Translator from "@/utils/common/Translator.ts";
import {FromGridAction} from "@/utils/UIConstants/enums.ts";
import FieldMetaData = namaTypes.FieldMetaData;
import FieldActionContextInfo = namaTypes.FieldActionContextInfo;
import EntityReferenceData = namaTypes.EntityReferenceData;
import GenericValue = namaTypes.GenericValue;

export default class ReferenceFieldWidgetUtil extends FieldWidgetUtil {
  referencedEntityType: ComputedRef<string>;

  constructor(presenter: WidgetsPresenter, fieldMetaData: FieldMetaData, defaultEntityType?: Ref<string | null | undefined>) {
    super(presenter, fieldMetaData);
    if (fieldMetaData.fieldType === FieldType.Genericreference) {
      this.referencedEntityType = computed(() => {
        let entityValue = NamaCommon.getFieldValue(this.presenter.fetchRecord(), this.fieldMetaData.fieldId! + "." + CommonFieldIds.ENTITY_TYPE);
        if (entityValue)
          return entityValue;
        if (defaultEntityType && defaultEntityType.value)
          return defaultEntityType.value;
        return "";
      });
    } else if (fieldMetaData.referencedEntityType && fieldMetaData.referencedEntityType.startsWith("$")) {
      const entityTypeField = fieldMetaData.referencedEntityType.substring(1);
      this.referencedEntityType = computed(() => presenter.fetchRecord()[entityTypeField]);
    }
    else
      this.referencedEntityType = computed(() => this.fieldMetaData.referencedEntityType ?? "");
  }

  static shouldShowImgInGrid(referencedEntityType: string) {
    if (!referencedEntityType)
      return false;
    let entityImageInfo = ClientCache.getEntityImageInfo(referencedEntityType);
    return entityImageInfo && entityImageInfo.useInGridColumns;
  }

  override formattedValue(): ComputedRef<any> {
    return computed(() => {
      let value = this.fetchFieldValue();
      if (value && SearchingAndSuggestionUtil.createOptionText(value))
        return SearchingAndSuggestionUtil.createOptionText(value);
      return null;
    });
  }

  override onUserInputFinished = async () => {
    let inputFieldValue = this.inputFieldValueRef.value;
    let originalValue = this.fetchFieldValue() as EntityReferenceData | null | undefined;
    if ((originalValue && inputFieldValue && inputFieldValue === originalValue.code) || !this.referencedEntityType.value)
      return;
    let searchContextInfo: FieldActionContextInfo = this.presenter.createSearchContextInfo(this.fieldId())
    let matchedRecord = await EditScreenFieldUtils.findRefByCode(this.referencedEntityType.value, inputFieldValue, searchContextInfo,this);
    await this.onSelect(matchedRecord, true);
  }

  onSelect = async (value?: EntityReferenceData, ignoreFieldsToUpdate?: boolean) => {
    let originalValue = this.fetchFieldValue() as EntityReferenceData | null | undefined;
    if (this.isEditOfDisabledFieldRejected(ReferenceFieldWidgetUtil.idOf(originalValue), ReferenceFieldWidgetUtil.idOf(value)))
      return;
    this.onPartialUserInput(value ? value.code : value);
    if (this.fieldMetaData.fieldType === FieldType.Genericreference && !value)
      NamaCommon.setFieldValue(this.presenter.fetchRecord(), this.fieldId(), {entityType: this.referencedEntityType.value} as EntityReferenceData);
    else
      NamaCommon.setFieldValue(this.presenter.fetchRecord(), this.fieldId(), value);
    if (originalValue !== value && (ReferenceFieldWidgetUtil.idOf(originalValue) !== ReferenceFieldWidgetUtil.idOf(value))) {
      const loadingStore = useLoadingStore();
      let dispatcher = this.presenter.fetchDispatcher();
      if (dispatcher) {
        loadingStore.startLoading();
        await dispatcher.validate(this.fieldId())
        if (value && value['fieldsToUpdate'] && !ignoreFieldsToUpdate)
          await dispatcher.onGuiActionUpdateList(value['fieldsToUpdate'] as namaTypes.ListOfGenericValues);
        loadingStore.stopLoading();
      }
    }
  }

  static idOf(value?: EntityReferenceData | null) {
    if (!value)
      return null;
    return value.id;
  }

  async searchFor(p: SearchForParams) {
    if (!this.isSuggestionProviderField)
      return await SearchingAndSuggestionUtil.searchFor(p);
    let placeExtraFilter;
    let contextFields: Record<string, GenericValue> = {};
    if (this.presenter.isEditPresenter()) {
      placeExtraFilter = (this.presenter as EditPresenter).getPlaceExtraFieldFilterId();
      this.presenter.fetchDispatcher().addContextFieldsToMap(this.fieldId(), this.createContextFieldsIds(), -1
        , contextFields, p.row !== -1 ? FromGridAction.Yes : FromGridAction.No, [], {});
    }
    let newVar = await SearchingAndSuggestionUtil.suggest({
      placeExtraFilter,
      fieldId: this.fieldId(),
      entityType: this.presenter.entityType,
      contextFields,
      currentText: p.query
    });
    let list = newVar.map(l => {
      if (l.vueValue) {
        if (l.fieldsToUpdate)
          l.vueValue['fieldsToUpdate'] = l.fieldsToUpdate;
        if (l.text)
          l.vueValue['optionCaption'] = Translator.getNameForFields(l.text);
      }
      return l.vueValue
    }) as EntityReferenceData[];
    SearchingAndSuggestionUtil.enhanceListForDisplaying(list, p);
    return list;
  }
}
